import { ReactNode, useEffect, useRef } from 'react';

import { Check, CircleHelp, KeyRound, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import logo from '../../../assets/logo-remente.svg';
import './redemption.css';

type Props = {
  children: ReactNode;
  step?: 0 | 1 | 2;
  showCodeNote?: boolean;
};

export const RedemptionLayout = ({ children, step, showCodeNote = false }: Props) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const content = useRef<HTMLElement>(null);
  const steps = [t('flow.account'), t('flow.redeem'), t('flow.download')];

  useEffect(() => {
    // Client-side navigation removes the previous control; announce the next screen.
    const heading = content.current?.querySelector('h1');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, step]);

  return (
    <div className="redemption-shell">
      <header className="redemption-header">
        <img src={logo} alt={t('flow.brand')} />
        <a href="mailto:support@remente.com">
          <CircleHelp size={17} aria-hidden="true" />
          {t('flow.help')}
        </a>
      </header>
      <main className="redemption-flow" ref={content}>
        {step !== undefined && (
          <ol className="redemption-progress" aria-label={t('flow.progress')}>
            {steps.map((label, index) => (
              <li
                key={label}
                className={index === step ? 'active' : index < step ? 'complete' : undefined}
                aria-current={index === step ? 'step' : undefined}
              >
                <span className="redemption-step-number" aria-hidden="true">
                  {index < step ? <Check size={14} /> : index + 1}
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="redemption-card">
          {children}
          {showCodeNote && (
            <p className="redemption-code-note">
              <KeyRound size={15} aria-hidden="true" />
              {t('flow.code_included')}
            </p>
          )}
        </div>
        {step !== undefined && (
          <p className="redemption-footer">
            <ShieldCheck size={14} aria-hidden="true" />
            {t('flow.account_note')}
          </p>
        )}
      </main>
    </div>
  );
};
